import React, { useEffect, useRef, useState } from "react";

// Scroll-triggered fade-up, as used throughout the Nudge landing page.
//
// Treated as progressive enhancement: the animation is a bonus, never a
// prerequisite for seeing the content. If IntersectionObserver is missing or
// never reports (offscreen rendering, prerendering, some embedded webviews),
// a rect check, a scroll listener and a safety timer all independently
// reveal the content, so it can't get stuck invisible.
const SAFETY_MS = 1200;

const Reveal = ({ children, delay = 0, className = "", as: Tag = "div" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      setShown(true);
      return;
    }

    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      setShown(true);
    };

    const inView = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh - 40 && r.bottom > 0;
    };

    // already on screen at mount
    if (inView()) show();

    let io;
    if (!done && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) show();
        },
        { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
      );
      io.observe(el);
    }

    const onScroll = () => {
      if (inView()) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // last resort — never leave content hidden
    const timer = setTimeout(show, SAFETY_MS);

    return () => {
      if (io) io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearTimeout(timer);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "is-visible" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
